(() => {
    const normalizeText = (value) =>
        String(value || "")
            .toLowerCase()
            .replace(/&/g, " and ")
            .replace(/[^a-z0-9]+/g, " ")
            .trim();

    const normalizeFilterValue = (value) =>
        String(value || "")
            .toLowerCase()
            .replace(/&/g, " and ")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .replace(/-{2,}/g, "-")
            .trim();

    const parsePageSize = (value) => {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 9;
    };

    const getTotalPages = (totalItems, pageSize, firstPageSize) => {
        if (totalItems <= 0) {
            return 0;
        }

        if (totalItems <= firstPageSize) {
            return 1;
        }

        return 1 + Math.ceil((totalItems - firstPageSize) / pageSize);
    };

    const getPageSlice = (currentPage, pageSize, firstPageSize) => {
        if (currentPage <= 1) {
            return { start: 0, end: firstPageSize };
        }

        const start = firstPageSize + (currentPage - 2) * pageSize;
        return { start, end: start + pageSize };
    };

    const getPageNumbers = (totalPages, currentPage) => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const pages = [1];
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        if (start > 2) {
            pages.push("...");
        }

        for (let page = start; page <= end; page += 1) {
            pages.push(page);
        }

        if (end < totalPages - 1) {
            pages.push("...");
        }

        pages.push(totalPages);
        return pages;
    };

    const createPageItem = (page, currentPage) => {
        const item = document.createElement("li");
        const button = document.createElement("button");
        const isActive = page === currentPage;

        item.className = isActive ? "active" : "";
        button.className = "query-pagination-page";
        button.classList.toggle("is-active", isActive);

        button.type = "button";
        button.dataset.page = String(page);
        button.setAttribute("aria-label", isActive ? `Current page, page ${page}` : `Go to page ${page}`);
        button.setAttribute("aria-current", isActive ? "page" : "false");
        button.innerHTML = `<span class="sr-only">page </span>${page}`;

        item.appendChild(button);
        return item;
    };

    const createEllipsisItem = () => {
        const item = document.createElement("li");
        item.className = "between";
        item.setAttribute("aria-hidden", "true");
        item.textContent = "...";
        return item;
    };

    const createArrowItem = (direction, disabled) => {
        const isPrevious = direction === "previous";
        const item = document.createElement("li");
        const button = document.createElement("button");
        const icon = document.createElement("iconify-icon");
        const srOnly = document.createElement("span");

        item.className = direction;
        button.type = "button";
        button.dataset.pageNav = direction;
        button.disabled = disabled;
        button.className = "query-pagination-arrow";
        button.classList.toggle("is-disabled", disabled);
        button.setAttribute("aria-label", isPrevious ? "Go to previous page" : "Go to next page");

        srOnly.className = "sr-only";
        srOnly.textContent = isPrevious ? "Previous" : "Next";

        icon.setAttribute("icon", isPrevious ? "solar:arrow-left-linear" : "solar:arrow-right-linear");
        icon.setAttribute("width", "20");

        button.appendChild(srOnly);
        button.appendChild(icon);
        item.appendChild(button);

        return item;
    };

    const setButtonState = (button, isActive) => {
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
        button.classList.toggle("is-active", isActive);
    };

    const getButtonFilter = (button) => {
        const source = button.dataset.filter || button.textContent;
        const normalized = normalizeFilterValue(source);
        return normalized === "all" ? null : normalized;
    };

    const initQuerySection = (section, sectionIndex) => {
        const filterButtons = Array.from(section.querySelectorAll(".filters button"));
        const searchInput = section.querySelector('input[type="text"]');
        const featuredArticle = section.querySelector(".featured-article");
        const list = section.querySelector("[data-query-results-list]");
        const pagination = section.querySelector(".pagination");
        const paginationList = pagination?.querySelector("ul");
        const emptyResults = section.querySelector(".empty-results");
        const resultsStatus = section.querySelector(".query-results-status");

        if (!list || !pagination || !paginationList) {
            return;
        }

        const items = Array.from(list.querySelectorAll(":scope > li")).filter((item) => item !== featuredArticle);

        if (!items.length) {
            return;
        }

        const pageSize = parsePageSize(pagination.dataset.pageSize);
        const isVideoQuery = section.classList.contains("Video");
        const sectionId = section.id || `query-section-${sectionIndex + 1}`;
        const listId = list.id || `${sectionId}-results`;
        const featuredItemId = featuredArticle?.id || "";
        let activeFilter = null;
        let activeFilterLabel = "All";
        let searchValue = "";
        let currentPage = 1;

        list.id = listId;

        filterButtons.forEach((button) => {
            button.type = "button";
            button.setAttribute("aria-controls", listId);
        });

        if (searchInput) {
            searchInput.setAttribute("aria-controls", listId);
        }

        pagination.setAttribute("aria-controls", listId);

        const getFilteredItems = () => {
            const normalizedSearch = normalizeText(searchValue);

            return items.filter((item) => {
                const itemType = normalizeFilterValue(item.dataset.type);
                const itemText = normalizeText(item.textContent);
                const matchesFilter = !activeFilter || itemType === activeFilter;
                const matchesSearch = !normalizedSearch || itemText.includes(normalizedSearch);

                return matchesFilter && matchesSearch;
            });
        };

        const getRenderableItems = (filteredItems, hasActiveQuery) => {
            if (!featuredItemId || hasActiveQuery) {
                return filteredItems;
            }

            return filteredItems.filter((item) => item.id !== featuredItemId);
        };

        const renderPagination = (totalPages) => {
            paginationList.innerHTML = "";

            const hasPages = totalPages > 0;
            const previous = createArrowItem("previous", !hasPages || currentPage <= 1);
            paginationList.appendChild(previous);

            if (hasPages) {
                const pageNumbers = getPageNumbers(totalPages, currentPage);

                pageNumbers.forEach((page) => {
                    if (page === "...") {
                        paginationList.appendChild(createEllipsisItem());
                        return;
                    }

                    paginationList.appendChild(createPageItem(page, currentPage));
                });
            }

            const next = createArrowItem("next", !hasPages || currentPage >= totalPages);
            paginationList.appendChild(next);
            pagination.classList.toggle("hidden", totalPages <= 1);
        };

        const render = () => {
            const filteredItems = getFilteredItems();
            const hasActiveFilter = Boolean(activeFilter) || Boolean(normalizeText(searchValue));
            const renderableItems = getRenderableItems(filteredItems, hasActiveFilter);
            const firstPageSize = isVideoQuery ? pageSize + 3 : pageSize;
            const featuredIsDeduped = renderableItems.length !== filteredItems.length;
            const totalPages = getTotalPages(renderableItems.length, pageSize, firstPageSize);

            if (currentPage > totalPages) {
                currentPage = totalPages > 0 ? totalPages : 1;
            }

            const { start, end } = getPageSlice(currentPage, pageSize, firstPageSize);
            const visibleItems = renderableItems.slice(start, end);
            const visibleSet = new Set(visibleItems);
            const isFeaturedVisible = Boolean(featuredArticle) && !hasActiveFilter && currentPage === 1;
            const totalResults = renderableItems.length + (featuredIsDeduped ? 1 : 0);
            const visibleCount = visibleItems.length + (isFeaturedVisible ? 1 : 0);

            items.forEach((item) => {
                item.classList.toggle("hidden", !visibleSet.has(item));
            });

            if (emptyResults) {
                emptyResults.classList.toggle("hidden", filteredItems.length > 0);
            }

            if (featuredArticle) {
                featuredArticle.classList.toggle("hidden", hasActiveFilter || currentPage !== 1);
            }

            if (resultsStatus) {
                if (!totalResults) {
                    resultsStatus.textContent = "No results found.";
                } else {
                    const filterContext = activeFilter ? ` Filter: ${activeFilterLabel}.` : "";
                    const searchContext = normalizeText(searchValue) ? ` Search: ${searchValue.trim()}.` : "";
                    resultsStatus.textContent = `Showing ${visibleCount} of ${totalResults} results. Page ${currentPage} of ${totalPages}.${filterContext}${searchContext}`.trim();
                }
            }

            renderPagination(totalPages);
        };

        const initiallyActiveButton =
            filterButtons.find((button) => button.getAttribute("aria-pressed") === "true") || filterButtons[0];

        if (initiallyActiveButton) {
            activeFilter = getButtonFilter(initiallyActiveButton);
            activeFilterLabel = initiallyActiveButton.textContent.trim();
        }

        filterButtons.forEach((button) => {
            setButtonState(button, button === initiallyActiveButton);

            button.addEventListener("click", () => {
                filterButtons.forEach((candidate) => setButtonState(candidate, candidate === button));

                activeFilter = getButtonFilter(button);
                activeFilterLabel = button.textContent.trim();
                currentPage = 1;
                render();
            });
        });

        if (searchInput) {
            searchInput.addEventListener("input", (event) => {
                searchValue = event.target.value || "";
                currentPage = 1;
                render();
            });
        }

        pagination.addEventListener("click", (event) => {
            const button = event.target.closest("button");

            if (!button || button.disabled) {
                return;
            }

            const filteredItems = getFilteredItems();
            const firstPageSize = isVideoQuery ? pageSize + 3 : pageSize;
            const totalPages = getTotalPages(filteredItems.length, pageSize, firstPageSize);

            if (button.dataset.page) {
                currentPage = Number.parseInt(button.dataset.page, 10) || 1;
                render();
                return;
            }

            if (button.dataset.pageNav === "previous" && currentPage > 1) {
                currentPage -= 1;
                render();
            }

            if (button.dataset.pageNav === "next" && currentPage < totalPages) {
                currentPage += 1;
                render();
            }
        });

        render();
    };

    document.querySelectorAll('section.query').forEach((section, index) => initQuerySection(section, index));
})();
